// More pictures and videos for one dish (ADR 0097), below its own photo.
//
// Folded away until opened, so a menu of thirty dishes does not ask for thirty lists.
// Several files can be chosen at once; they upload one after another, each with its
// own progress and its own result, so one bad file does not stop the rest. A file
// the server is still processing shows as such and the list looks again every few
// seconds until nothing is left processing.

import { useRef, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../../api/client.js';
import { MEDIA_LIMITS, checkMediaFile, uploadItemMedia } from '../../api/media-upload.js';
import type { ItemMediaView } from '../../api/types.js';

const POLL_MS = 3000;

// What the worker reports, in words an owner can act on.
export const FAILURE_TEXT: Record<string, string> = {
  unreadable: 'We could not read this file. Try a JPEG or PNG picture, or an MP4 video.',
  unsupported_format: 'This video format is not supported. Use MP4, MOV or WebM.',
  no_video_stream: 'This file has no picture in it.',
  too_long: 'Videos can be up to 20 seconds. Trim it and add it again.',
  too_large_output: 'This video is too large even after shrinking. Record a shorter clip.',
  timeout: 'Processing took too long. Try a shorter clip.',
  processing_failed: 'We could not process this file. Remove it and try again.',
  upload_missing: 'The upload did not arrive. Remove it and add it again.',
  video_disabled: 'Videos are switched off on this server for now.',
};
const failureText = (code: string | null): string =>
  (code && FAILURE_TEXT[code]) || 'This file could not be used. Remove it and try again.';

interface UploadJob {
  readonly id: number;
  readonly name: string;
  readonly progress: number;
  readonly status: 'uploading' | 'done' | 'failed';
  readonly message?: string;
}

export function ItemMediaEditor(props: {
  readonly orgId: string;
  readonly menuId: string;
  readonly itemId: string;
  readonly itemName: string;
}): JSX.Element {
  const { orgId, menuId, itemId, itemName } = props;
  const basePath = `/organizations/${orgId}/menus/${menuId}/items/${itemId}`;
  const listKey = ['item-media', orgId, menuId, itemId] as const;
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState<readonly UploadJob[]>([]);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const nextJob = useRef(1);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const list = useQuery({
    queryKey: listKey,
    enabled: open,
    queryFn: () => apiFetch<{ media: readonly ItemMediaView[] }>(`${basePath}/media`),
    refetchInterval: (q) =>
      (q.state.data?.media ?? []).some((m) => m.state === 'processing') ? POLL_MS : false,
    refetchIntervalInBackground: false,
  });
  const media = list.data?.media ?? [];
  const videos = media.filter((m) => m.kind === 'video').length;
  const full = media.length >= MEDIA_LIMITS.maxPerItem;

  const remove = useMutation({
    mutationFn: (mediaId: string) =>
      apiFetch<unknown>(`${basePath}/media/${mediaId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setRemoving(null);
      return qc.invalidateQueries({ queryKey: listKey });
    },
  });

  function patch(id: number, change: Partial<UploadJob>): void {
    setJobs((all) => all.map((j) => (j.id === id ? { ...j, ...change } : j)));
  }

  async function onChoose(files: FileList | null): Promise<void> {
    const chosen = Array.from(files ?? []);
    if (inputRef.current) inputRef.current.value = '';
    if (chosen.length === 0) return;
    setBusy(true);
    let room = MEDIA_LIMITS.maxPerItem - media.length;
    let videoRoom = MEDIA_LIMITS.maxVideosPerItem - videos;
    for (const file of chosen) {
      const id = nextJob.current++;
      const check = checkMediaFile(file);
      const refusal = !check.ok
        ? check.message
        : room <= 0
          ? `A dish can have up to ${MEDIA_LIMITS.maxPerItem} extra pictures and videos.`
          : check.kind === 'video' && videoRoom <= 0
            ? `A dish can have up to ${MEDIA_LIMITS.maxVideosPerItem} videos.`
            : null;
      if (refusal) {
        setJobs((all) => [
          ...all,
          { id, name: file.name, progress: 0, status: 'failed', message: refusal },
        ]);
        continue;
      }
      setJobs((all) => [...all, { id, name: file.name, progress: 0, status: 'uploading' }]);
      try {
        await uploadItemMedia({ basePath, file, onProgress: (f) => patch(id, { progress: f }) });
        room -= 1;
        if (check.ok && check.kind === 'video') videoRoom -= 1;
        patch(id, { progress: 1, status: 'done' });
        await qc.invalidateQueries({ queryKey: listKey });
      } catch (err) {
        patch(id, {
          status: 'failed',
          message:
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'upload failed',
        });
      }
    }
    setBusy(false);
  }

  return (
    <div className="media-editor">
      <button
        type="button"
        className="button secondary"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? 'Hide pictures and videos' : 'More pictures and videos'}
      </button>

      {open ? (
        <div className="media-panel">
          {list.isLoading ? <p className="muted">Loading…</p> : null}
          {list.error ? <p className="error">Could not load the list.</p> : null}

          {media.length > 0 ? (
            <ul className="media-list">
              {media.map((m) => (
                <MediaTile
                  key={m.id}
                  m={m}
                  itemName={itemName}
                  asking={removing === m.id}
                  onAsk={() => setRemoving(m.id)}
                  onKeep={() => setRemoving(null)}
                  onRemove={() => remove.mutate(m.id)}
                  removing={remove.isPending && removing === m.id}
                />
              ))}
            </ul>
          ) : list.data ? (
            <p className="muted">No extra pictures or videos yet.</p>
          ) : null}
          {remove.error ? (
            <p className="error">
              {remove.error instanceof ApiError ? remove.error.message : 'could not remove it'}
            </p>
          ) : null}

          <label className="media-add">
            <span>
              {full
                ? `This dish has all ${MEDIA_LIMITS.maxPerItem} it can hold. Remove one to add another.`
                : 'Add pictures or videos'}
            </span>
            <input
              ref={inputRef}
              type="file"
              multiple
              aria-label={`add pictures or videos to ${itemName}`}
              accept="image/jpeg,image/png,image/webp,image/avif,video/mp4,video/quicktime,video/webm"
              disabled={busy || full}
              onChange={(e) => void onChoose(e.target.files)}
            />
          </label>
          <p className="muted">
            Up to {MEDIA_LIMITS.maxPerItem} in all, {MEDIA_LIMITS.maxVideosPerItem} of them videos.
            Pictures up to 10 MB. Videos up to 20 seconds. They appear on the menu after you
            publish.
          </p>

          {jobs.length > 0 ? (
            <ul className="media-jobs" aria-label="uploads">
              {jobs.map((j) => (
                <li key={j.id} className={`media-job ${j.status}`}>
                  <span>{j.name}</span>{' '}
                  {j.status === 'uploading' ? (
                    <progress max={1} value={j.progress} aria-label={`uploading ${j.name}`} />
                  ) : j.status === 'done' ? (
                    <span className="muted">added</span>
                  ) : (
                    <span className="error" role="alert">
                      {j.message}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MediaTile(props: {
  readonly m: ItemMediaView;
  readonly itemName: string;
  readonly asking: boolean;
  readonly removing: boolean;
  readonly onAsk: () => void;
  readonly onKeep: () => void;
  readonly onRemove: () => void;
}): JSX.Element {
  const { m, itemName, asking, removing, onAsk, onKeep, onRemove } = props;
  const thumb =
    m.kind === 'video'
      ? m.files.find((f) => f.file === 'poster.webp')
      : (m.files.find((f) => f.file === '320.webp') ??
        m.files.find((f) => f.contentType === 'image/webp'));
  const seconds = m.durationMs ? Math.round(m.durationMs / 1000) : null;
  return (
    <li className={`media-tile ${m.state}`}>
      {m.state === 'ready' && thumb ? (
        <div className="media-thumb">
          <img src={thumb.url} alt="" width={96} height={72} />
          {m.kind === 'video' ? (
            <span className="media-badge">▶ {seconds ? `${seconds} s` : 'video'}</span>
          ) : null}
        </div>
      ) : (
        <div className="media-thumb empty">
          {m.state === 'processing' ? 'Processing…' : 'Failed'}
        </div>
      )}
      <div className="media-meta">
        <span>
          {m.kind === 'video' ? 'Video' : 'Picture'}
          {m.state === 'ready' ? ' · ready' : ''}
        </span>
        {m.state === 'failed' ? (
          <span className="error" role="alert">
            {failureText(m.failureCode)}
          </span>
        ) : null}
        {asking ? (
          <span
            className="row"
            role="alertdialog"
            aria-label={`Remove this ${m.kind} from ${itemName}`}
          >
            <span>Remove this {m.kind === 'video' ? 'video' : 'picture'}?</span>
            <button type="button" disabled={removing} onClick={onRemove}>
              {removing ? 'Removing…' : 'Yes, remove'}
            </button>
            <button type="button" onClick={onKeep}>
              Keep
            </button>
          </span>
        ) : (
          <button type="button" onClick={onAsk}>
            Remove
          </button>
        )}
      </div>
    </li>
  );
}
